import { Grid } from "@mui/material";
import InputGroup from "../../../components/FormComponent/InputGroup";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button/Button";
import { Icon } from "@iconify/react";
import styles from "../settings.module.css";
import { useState, useEffect } from "react";
import { httprequest } from "../../../data/api";
import { toast, ToastContainer } from "react-toastify";

const Incentives = () => {
  // Student Point Cap
  const [pointCap, setPointCap] = useState({
    daily: "50",
    weekly: "200"
  });

  // Duplication Rule
  const [duplicationRule, setDuplicationRule] = useState({
    preventDuplicateAwards: true
  });

  // Finance and Risk
  const [finance, setFinance] = useState({
    redemptionWindow: "Monthly",
    minRedeemable: "200 pts",
    paymentMethod: "Bank",
    requireApproval: true,
    approvalThreshold: "50",
    conversionRate: "100"
  });

  // Content Quality
  const [contentQuality, setContentQuality] = useState({
    minWords: "",
    detectLowEffort: true,
    detectDuplicate: true
  });

  const [mixedSignals, setMixedSignals] = useState({
    autoApproveFrom: "",
    autoApproveTo: "",
    holdFrom: "",
    holdTo: "",
    blockFrom: "",
    blockTo: "",
    notifyTeacher: true,
    allowAdminOverride: true
  });

  const GET_REWARD_SETTINGS = "/admin/v1/all-reward-settings";
  const GET_ALL_TAGS = "/admin/v1/reward-tags";
  const CREATE_TAG = "/admin/v1/points/tags";
  const UPDATE_TAG = "/admin/v1/points/tags";

  // Tags
  const [tags, setTags] = useState([]);
  const [isTagLoading, setIsTagLoading] = useState(false);
  const [isEditTagLoading, setIsEditTagLoading] = useState(false);

  const fetchTags = () => {
    httprequest
      .get(GET_ALL_TAGS)
      .then((res) => {
        const data = res?.data?.data || [];
        setTags(
          data.map((tag) => ({
            id: tag.id || tag._id || Date.now(),
            name: tag.name,
            maxPoint: String(tag.max_points ?? "")
          }))
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load tags");
      });
  };

  const fetchRewardSettings = () => {
    httprequest
      .get(GET_REWARD_SETTINGS)
      .then((res) => {
        const payload = res?.data?.data;
        const data = Array.isArray(payload) && payload.length > 0 ? payload[0] : (payload || {});

        const studentLimits = data.student_limits || {};
        setPointCap({
          daily: String(studentLimits.daily ?? ""),
          weekly: String(studentLimits.weekly ?? "")
        });

        const redemptionWindowMap = {
          monthly: "Monthly",
          weekly: "Weekly",
          "bi-weekly": "Bi-Weekly",
          quarterly: "Quarterly"
        };

        const paymentMethodMap = {
          bank: "Bank",
          cash: "Cash",
          mobile_money: "Mobile Money"
        };

        const financeData = data.finance || {};

        setFinance((prev) => ({
          ...prev,
          redemptionWindow:
            redemptionWindowMap[(financeData.redemption_window || "").toLowerCase()] || prev.redemptionWindow,
          minRedeemable:
            String(financeData.min_payable_points ?? prev.minRedeemable),
          paymentMethod:
            paymentMethodMap[(financeData.payment_method || "").toLowerCase()] || prev.paymentMethod,
          requireApproval:
            financeData.require_manual_approval == null ? prev.requireApproval : Boolean(financeData.require_manual_approval),
          conversionRate:
            String(financeData.conversion_rate ?? prev.conversionRate)
        }));

        setDuplicationRule((prev) => ({
          ...prev,
          preventDuplicateAwards:
            data.prevent_duplicate_awards == null ? prev.preventDuplicateAwards : Boolean(data.prevent_duplicate_awards)
        }));

        const contentData = data.content_quality || {};

        setContentQuality((prev) => ({
          ...prev,
          minWords:
            String(contentData.min_lesson_note_words ?? prev.minWords),
          detectLowEffort:
            contentData.detect_low_effort_content == null ? prev.detectLowEffort : Boolean(contentData.detect_low_effort_content),
          detectDuplicate:
            contentData.detect_duplicate_content == null ? prev.detectDuplicate : Boolean(contentData.detect_duplicate_content)
        }));

        const riskData = data.risk_controls || {};

        setMixedSignals((prev) => ({
          ...prev,
          holdFrom:
            String(riskData.hold_threshold ?? prev.holdFrom),
          blockFrom:
            String(riskData.block_threshold ?? prev.blockFrom),
          notifyTeacher:
            riskData.notify_teacher == null ? prev.notifyTeacher : Boolean(riskData.notify_teacher),
          allowAdminOverride:
            riskData.allow_admin_override == null ? prev.allowAdminOverride : Boolean(riskData.allow_admin_override)
        }));
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to load reward settings");
      });
  };

  useEffect(() => {
    fetchTags();
    fetchRewardSettings();
  }, []);

  const [currentTag, setCurrentTag] = useState({});
  const [tagForm, setTagForm] = useState({ name: "", maxPoint: "" });

  // Tag Modals
  const [createTagModal, setCreateTagModal] = useState(false);
  const [editTagModal, setEditTagModal] = useState(false);
  const [deleteTagModal, setDeleteTagModal] = useState(false);

  const handleCreateTagModal = () => {
    setTagForm({ name: "", maxPoint: "" });
    setCreateTagModal(!createTagModal);
  };

  const handleEditTagModal = (tag) => {
    setCurrentTag(tag);
    setTagForm({ name: tag.name, maxPoint: tag.maxPoint });
    setEditTagModal(!editTagModal);
  };

  const handleCloseEditModal = () => {
    setEditTagModal(false);
  };

  const handleDeleteTagModal = (tag) => {
    setCurrentTag(tag);
    setDeleteTagModal(!deleteTagModal);
  };

  const handleCloseDeleteModal = () => {
    setDeleteTagModal(false);
  };

  const handleCreateTag = () => {
    if (!tagForm.name || !tagForm.maxPoint) {
      toast.error("Tag name and max point are required.");

      return;
    }
    setIsTagLoading(true);
    httprequest
      .post(CREATE_TAG, {
        tags: [{ name: tagForm.name, max_points: Number(tagForm.maxPoint) }]
      })
      .then((res) => {
        setIsTagLoading(false);
        toast.success(res?.data?.message || "Tag created successfully");
        setTagForm({ name: "", maxPoint: "" });
        setCreateTagModal(false);
        fetchTags();
      })
      .catch((err) => {
        setIsTagLoading(false);
        toast.error(err?.response?.data?.message || "Failed to create tag");
      });
  };

  const handleSaveEditTag = () => {
    if (!currentTag?.id) {
      toast.error("Invalid tag selected.");

      return;
    }

    if (!tagForm.name || !tagForm.maxPoint) {
      toast.error("Tag name and max point are required.");

      return;
    }

    setIsEditTagLoading(true);
    httprequest
      .patch(`${UPDATE_TAG}/${currentTag.id}`, {
        name: tagForm.name,
        max_points: Number(tagForm.maxPoint)
      })
      .then((res) => {
        setIsEditTagLoading(false);
        toast.success(res?.data?.message || "Tag updated successfully");
        setEditTagModal(false);
        fetchTags();
      })
      .catch((err) => {
        setIsEditTagLoading(false);
        toast.error(err?.response?.data?.message || "Failed to update tag");
      });
  };

  const handleDeleteTag = () => {
    setTags(tags.filter((tag) => tag.id !== currentTag.id));
    setDeleteTagModal(false);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = () => {
    setIsSaving(true);
    const paymentMethodPayloadMap = {
      Bank: "bank",
      Cash: "cash",
      "Mobile Money": "mobile_money"
    };

    const payload = {
      student_limits: {
        daily: Number(pointCap.daily),
        weekly: Number(pointCap.weekly)
      },
      prevent_duplicate_awards: duplicationRule.preventDuplicateAwards,
      content_quality: {
        min_lesson_note_words: Number(contentQuality.minWords),
        detect_low_effort_content: contentQuality.detectLowEffort,
        detect_duplicate_content: contentQuality.detectDuplicate
      },
      risk_controls: {
        hold_threshold: Number(mixedSignals.holdFrom),
        block_threshold: Number(mixedSignals.blockFrom),
        notify_teacher: mixedSignals.notifyTeacher,
        allow_admin_override: mixedSignals.allowAdminOverride
      },
      finance: {
        redemption_window: finance.redemptionWindow.toLowerCase(),
        min_payable_points: Number(String(finance.minRedeemable).replace(/\D/g, "")),
        payment_method: paymentMethodPayloadMap[finance.paymentMethod] || finance.paymentMethod.toLowerCase(),
        require_manual_approval: finance.requireApproval,
        conversion_rate: Number(String(finance.conversionRate).replace(/\D/g, ""))
      }
    };

    httprequest
      .patch("/admin/v1/reward-settings", payload)
      .then((res) => {
        setIsSaving(false);
        toast.success(res?.data?.message || "Settings updated successfully");
      })
      .catch((err) => {
        setIsSaving(false);
        toast.error(err?.response?.data?.message || "Failed to update settings");
      });
  };

  return (
    <div>
      <div className={styles.incentiveSection}>
        <h4 className={styles.changepassword}>Student point cap</h4>
        <p className={styles.passwordtext}>
          Set maximum points a student can earn. Once a cap is reached, additional points will be blocked.
        </p>
        <Grid container spacing={2} className="input-padding">
          <Grid item lg={4} md={6} sm={6} xs={12}>
            <InputGroup
              label="Max points per student (daily)"
              inputType="number"
              inputName="daily"
              value={pointCap.daily}
              onChange={(e) => setPointCap({ ...pointCap, daily: e.target.value })}
            />
          </Grid>
          <Grid item lg={4} md={6} sm={6} xs={12}>
            <InputGroup
              label="Max points per student (weekly)"
              inputType="number"
              inputName="weekly"
              value={pointCap.weekly}
              onChange={(e) => setPointCap({ ...pointCap, weekly: e.target.value })}
            />
          </Grid>
        </Grid>
      </div>

      <div className={styles.incentiveSection}>
        <div className="flex justify-items-space align-items-center">
          <div>
            <h4 className={styles.changepassword}>Tags</h4>
            <p className={styles.passwordtext}>
              Teachers can only award points using admin-approved tags.
            </p>
          </div>
          {/* <button className={styles.addTagBtn} onClick={handleCreateTagModal}>
            <Icon icon="ic:baseline-plus" width="18" height="18" />
            Add new Tag
          </button> */}
        </div>
        <table className={styles.tagTable}>
          <thead>
            <tr>
              <th>
                Tag Name <Icon icon="mdi:chevron-down" width="14" height="14" />
              </th>
              <th>
                Max Point <Icon icon="mdi:chevron-down" width="14" height="14" />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => (
              <tr key={tag.id}>
                <td>{tag.name}</td>
                <td>{tag.maxPoint}</td>
                <td>
                  <div className="table-action-container">
                    <button className="table-action-icon" onClick={() => handleEditTagModal(tag)}>
                      <Icon icon="nimbus:edit" />
                    </button>
                    <button
                      className="table-action-icon delete-icon"
                      onClick={() => handleDeleteTagModal(tag)}
                    >
                      <Icon icon="mingcute:delete-2-line" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Finance and Risk */}
      <div className={styles.incentiveSection}>
        <h4 className={styles.changepassword}>Finance and Risk</h4>
        <p className={styles.passwordtext}>
          Configure teachers incentives, redemption rules, and financial safeguards.
        </p>
        <Grid container spacing={2} className="input-padding">
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <SelectGroup label="Redemption Window">
              <select
                value={finance.redemptionWindow}
                onChange={(e) => setFinance({ ...finance, redemptionWindow: e.target.value })}
              >
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-Weekly">Bi-Weekly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </SelectGroup>
          </Grid>
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <InputGroup
              label="Minimum redeemable amount"
              inputType="text"
              inputName="minRedeemable"
              value={finance.minRedeemable}
              onChange={(e) => setFinance({ ...finance, minRedeemable: e.target.value })}
            />
          </Grid>
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <InputGroup
              label="Conversion rate (1 point = ₦)"
              inputType="number"
              inputName="conversionRate"
              value={finance.conversionRate}
              onChange={(e) => setFinance({ ...finance, conversionRate: e.target.value })}
            />
          </Grid>
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <SelectGroup label="Payment method">
              <select
                value={finance.paymentMethod}
                onChange={(e) => setFinance({ ...finance, paymentMethod: e.target.value })}
              >
                <option value="Bank">Bank</option>
                <option value="Cash">Cash</option>
                <option value="Mobile Money">Mobile Money</option>
              </select>
            </SelectGroup>
          </Grid>
        </Grid>
        <div className={`${styles.approvalRow} input-padding`}>
          <label className={`${styles.picks} flex align-items-center`}>
            <input
              type="checkbox"
              checked={finance.requireApproval}
              onChange={(e) => setFinance({ ...finance, requireApproval: e.target.checked })}
            />
            <span>Require manual approval for redemptions above (₦)</span>
          </label>
          <input
            className={styles.smallInput}
            type="number"
            value={finance.approvalThreshold}
            onChange={(e) => setFinance({ ...finance, approvalThreshold: e.target.value })}
          />
        </div>
      </div>

      {/* Duplication Rule */}
      <div className={styles.incentiveSection}>
        <h4 className={styles.changepassword}>Duplication rule</h4>
        <p className={styles.passwordtext}>Prevent accidental or duplicate point awards.</p>
        <div className="input-padding">
          <label className={`${styles.picks} flex align-items-center`}>
            <input
              type="checkbox"
              checked={duplicationRule.preventDuplicateAwards}
              onChange={(e) => setDuplicationRule({ ...duplicationRule, preventDuplicateAwards: e.target.checked })}
            />
            <span>Prevent duplicate awards per Teacher + Class + Date + Topic</span>
          </label>
        </div>
      </div>

      {/* Content Quality */}
      <div className={styles.incentiveSection}>
        <h4 className={styles.changepassword}>Content Quality</h4>
        <p className={styles.passwordtext}>
          Detect low-effort, duplicate, or abusive content while protecting normal teaching behaviour.
        </p>
        <div className="input-padding">
          <div className="flex align-items-center gap-5" style={{ marginBottom: 16 }}>
            <span className={styles.ruleText}>Lesson note less than</span>
            <input
              className={styles.smallInput}
              type="number"
              value={contentQuality.minWords}
              onChange={(e) => setContentQuality({ ...contentQuality, minWords: e.target.value })}
            />
            <span className={styles.ruleText}>word</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label className={`${styles.picks} flex align-items-center`}>
              <input
                type="checkbox"
                checked={contentQuality.detectLowEffort}
                onChange={(e) => setContentQuality({ ...contentQuality, detectLowEffort: e.target.checked })}
              />
              <span>Detect low-effort lesson content</span>
            </label>
            <label className={`${styles.picks} flex align-items-center`}>
              <input
                type="checkbox"
                checked={contentQuality.detectDuplicate}
                onChange={(e) => setContentQuality({ ...contentQuality, detectDuplicate: e.target.checked })}
              />
              <span>Detect duplicate or highly similar content</span>
            </label>
          </div>
        </div>
      </div>

      <Grid container spacing={4}>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <div className={styles.incentiveSection}>
            <h4 className={styles.changepassword}>Mixed Signals</h4>
            <p className={styles.passwordtext}>
              Combines multiple weak signals to detect abuse without penalizing normal behaviour.
            </p>
            <div className="input-padding">
              <div className={styles.rangeGroup}>
                <label className={styles.rangeLabel}>Auto-Approve</label>
                <div className="flex align-items-center gap-5">
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.autoApproveFrom}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, autoApproveFrom: e.target.value })}
                  />
                  <span className={styles.ruleText}>to</span>
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.autoApproveTo}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, autoApproveTo: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.rangeGroup}>
                <label className={styles.rangeLabel}>Hold for review</label>
                <div className="flex align-items-center gap-5">
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.holdFrom}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, holdFrom: e.target.value })}
                  />
                  <span className={styles.ruleText}>to</span>
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.holdTo}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, holdTo: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.rangeGroup}>
                <label className={styles.rangeLabel}>Block</label>
                <div className="flex align-items-center gap-5">
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.blockFrom}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, blockFrom: e.target.value })}
                  />
                  <span className={styles.ruleText}>to</span>
                  <input
                    className={styles.rangeInput}
                    type="number"
                    value={mixedSignals.blockTo}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, blockTo: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <div className={styles.incentiveSection}>

            <h4 className={styles.changepassword}>When Held</h4>
            <div className="input-padding">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label className={`${styles.picks} flex align-items-center`}>
                  <input
                    type="checkbox"
                    checked={mixedSignals.notifyTeacher}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, notifyTeacher: e.target.checked })}
                  />
                  <span>Notify Teacher</span>
                </label>
                <label className={`${styles.picks} flex align-items-center`}>
                  <input
                    type="checkbox"
                    checked={mixedSignals.allowAdminOverride}
                    onChange={(e) => setMixedSignals({ ...mixedSignals, allowAdminOverride: e.target.checked })}
                  />
                  <span>Allow Admin Override</span>
                </label>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", marginBottom: "20px" }}>
        <Button
          variant="primary"
          buttonName={isSaving ? "Saving..." : "Save Settings"}
          onClick={handleSaveSettings}
          disabled={isSaving}
          color="btndefault"
        />
      </div>

      {/* Create Tag Modal */}
      <Modal
        title="Create a tag"
        subtitle={
          <div>Here you can save and embedded the code for email templates.</div>
        }
        modalContent={
          <>
            <Grid container spacing={2} className="input-padding">
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  label="Tag Name"
                  margin="normal"
                  placeHolder="Enter tag name"
                  inputType="text"
                  inputName="name"
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  label="Max Point Allowed"
                  margin="normal"
                  placeHolder="50"
                  inputType="number"
                  inputName="maxPoint"
                  value={tagForm.maxPoint}
                  onChange={(e) => setTagForm({ ...tagForm, maxPoint: e.target.value })}
                />
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <Button
              onClick={handleCreateTag}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName={isTagLoading ? "Creating..." : "Create Tag"}
              disabled={isTagLoading}
            />
          </>
        }
        isopen={createTagModal}
        closeModal={handleCreateTagModal}
      />

      {/* Edit Tag Modal */}
      <Modal
        title="Edit tag"
        subtitle={
          <div>Update the tag details below.</div>
        }
        modalContent={
          <>
            <Grid container spacing={2} className="input-padding">
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  label="Tag Name"
                  margin="normal"
                  placeHolder="Enter tag name"
                  inputType="text"
                  disabled
                  inputName="name"
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  label="Max Point Allowed"
                  margin="normal"
                  placeHolder="50"
                  inputType="number"
                  inputName="maxPoint"
                  value={tagForm.maxPoint}
                  onChange={(e) => setTagForm({ ...tagForm, maxPoint: e.target.value })}
                />
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <Button
              onClick={handleSaveEditTag}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName={isEditTagLoading ? "Updating..." : "Update Tag"}
              disabled={isEditTagLoading}
            />
          </>
        }
        isopen={editTagModal}
        closeModal={handleCloseEditModal}
      />

      {/* Delete Tag Modal */}
      <Modal
        title="Delete this Tag"
        subtitle={
          <div>Are you sure you want to delete <span className="modal-name-highlight">{currentTag?.name}</span> tag? If you delete it you won't have access to it anymore.</div>
        }
        modalContent={
          <></>
        }
        modalFooter={
          <>
            <div className="flex gap-10">
              <Button
                variant="dangeroutline"
                buttonSize="full"
                color="btndanger"
                buttonName="Cancel"
                onClick={handleCloseDeleteModal}
              />
              <Button
                variant="danger"
                buttonName="Delete Tag"
                buttonSize="full"
                color="btndefault"
                onClick={handleDeleteTag}
              />
            </div>
          </>
        }
        isopen={deleteTagModal}
        closeModal={handleCloseDeleteModal}
      />
      <ToastContainer />
    </div>
  );
};

export default Incentives;
